import { apiBasePath } from '../baseUrl';
import { apiUrl } from '../../utils/api';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { normalizeCreditCardValues } from '../../utils/valueSelection';

export const extendedApi = apiBasePath.injectEndpoints({
  endpoints: (build) => ({
    getMonthlyPostings: build.query({
      query: () => apiUrl.glPostingsMonthly,
      transformResponse: (res) => res?.data?.postings ?? [],
      providesTags: ['MonthlyPostings'],
    }),
    getReconciliationSummary: build.query({
      query: ({ productId, startDate, endDate }) => {
        const params = new URLSearchParams();
        if (productId != null) params.set('productId', String(productId));
        if (startDate != null) params.set('startDate', String(startDate));
        if (endDate != null) params.set('endDate', String(endDate));
        return `${apiUrl.reconciliationSummary}?${params.toString()}`;
      },
      transformResponse: (res) => res ?? {},
    }),
    getMonthlyReconciliationFeeds: build.mutation({
      query: (body) => ({
        url: apiUrl.glPostingsMonthlyFeeds,
        method: 'POST',
        body: {
          productId: body.productId,
          x3ProductName: body.x3ProductName,
          startDate: body.startDate,
          endDate: body.endDate,
        },
      }),
      transformResponse: (res) => res?.data ?? {},
    }),
    getFinalizedMonthlyPostings: build.query({
      query: (params = {}) => {
        const q = new URLSearchParams();
        if (params.search) q.set('search', params.search);
        if (params.x3ProductNames) q.set('x3ProductNames', params.x3ProductNames);
        if (params.startDate) q.set('startDate', params.startDate);
        if (params.endDate) q.set('endDate', params.endDate);
        if (params.finalizedBy != null) q.set('finalizedBy', String(params.finalizedBy));
        q.set('page', String(params.page ?? DEFAULT_PAGE));
        q.set('perPage', String(params.perPage ?? DEFAULT_PER_PAGE));
        if (params.sort) q.set('sort', params.sort);
        return `${apiUrl.glPostingsMonthly}?${q.toString()}`;
      },
      transformResponse: (res) =>
        res?.data ?? { postings: [], pagination: { total: 0, page: 1, perPage: 10, pages: 0 } },
      providesTags: ['FinalizedMonthlyPostings'],
    }),
    getMonthlyPostingById: build.query({
      query: (id) => `${apiUrl.glPostingsMonthly}/${id}`,
      transformResponse: (res) => {
        const data = res?.data ?? res;
        if (!data) return null;
        const posting = data.posting ?? data.record ?? data.monthlyPosting ?? data;
        if (!posting) return null;

        return {
          ...posting,
          // Normalize to creditCard* for callers; prefer new API cc* when both exist.
          ...normalizeCreditCardValues(posting),
          availableSublocations: Array.isArray(data?.sublocations)
            ? data.sublocations
            : Array.isArray(posting?.availableSublocations)
            ? posting.availableSublocations
            : [],
        };
      },
      providesTags: (result, error, id) => [{ type: 'FinalizedMonthlyPostings', id }],
    }),
    finalizeMonthlyReconciliation: build.mutation({
      query: (body) => ({
        url: apiUrl.glPostingsMonthly,
        method: 'POST',
        body: {
          productId: body.productId,
          x3ProductName: body.x3ProductName,
          startDate: body.startDate,
          endDate: body.endDate,
          postingDate: body.postingDate,
        },
      }),
      transformResponse: (res) => res ?? {},
      invalidatesTags: ['MonthlyPostings', 'FinalizedMonthlyPostings'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMonthlyPostingsQuery,
  useLazyGetMonthlyPostingsQuery,
  useLazyGetReconciliationSummaryQuery,
  useGetMonthlyReconciliationFeedsMutation,
  useGetFinalizedMonthlyPostingsQuery,
  useLazyGetFinalizedMonthlyPostingsQuery,
  useGetMonthlyPostingByIdQuery,
  useFinalizeMonthlyReconciliationMutation,
} = extendedApi;
